import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { adminApi, type UserPermissions } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  ScrollText,
  KeyRound,
  Settings,
  CreditCard,
  Wallet,
  ArrowLeft,
  Menu,
  X,
  Shield,
  ChevronDown,
  BarChart3,
  BookOpen,
  ClipboardList,
  Mail,
} from "lucide-react";
import logoSvg from "@/assets/logo.svg";

// ═══════════════════════════════════════════════════════════════════
// Navigation structure
// ═══════════════════════════════════════════════════════════════════

interface NavItemDef {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  permission: string | null;
}

interface NavItemEntry extends NavItemDef {
  type: "item";
}

interface NavGroupEntry {
  type: "group";
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  permission: string | null;
  children: NavItemDef[];
}

type NavEntry = NavItemEntry | NavGroupEntry;

const adminNavigation: NavEntry[] = [
  {
    type: "item",
    name: "Dashboard",
    href: "/admin",
    icon: LayoutDashboard,
    permission: null,
  },
  {
    type: "group",
    name: "Användare",
    href: "/admin/users",
    icon: Users,
    permission: "users",
    children: [
      { name: "Företag", href: "/admin/companies", icon: Building2, permission: "companies" },
      { name: "Årsredovisningar", href: "/admin/reports", icon: FileText, permission: "reports" },
      { name: "Aktieböcker", href: "/admin/share-books", icon: BookOpen, permission: "share_books" },
      { name: "Protokoll", href: "/admin/protocols", icon: ClipboardList, permission: "protocols" },
    ],
  },
  {
    type: "item",
    name: "Prenumerationer",
    href: "/admin/subscriptions",
    icon: Wallet,
    permission: "subscriptions",
  },
  {
    type: "group",
    name: "Inställningar",
    href: "/admin/settings",
    icon: Settings,
    permission: "settings",
    children: [
      { name: "Prissättning", href: "/admin/pricing", icon: CreditCard, permission: "pricing" },
      { name: "Roller", href: "/admin/roles", icon: KeyRound, permission: "roles" },
      { name: "E-post & Cron", href: "/admin/email-cron", icon: Mail, permission: "settings" },
    ],
  },
  {
    type: "item",
    name: "Statistik",
    href: "/admin/statistics",
    icon: BarChart3,
    permission: "statistics",
  },
  {
    type: "item",
    name: "Systemloggar",
    href: "/admin/audit-logs",
    icon: ScrollText,
    permission: "audit_logs",
  },
];

// ═══════════════════════════════════════════════════════════════════
// Sidebar content (shared between desktop & mobile)
// ═══════════════════════════════════════════════════════════════════

function SidebarContent({
  navigation,
  isActiveLink,
  expandedGroups,
  toggleGroup,
}: {
  navigation: NavEntry[];
  isActiveLink: (path: string) => boolean;
  expandedGroups: Set<string>;
  toggleGroup: (name: string) => void;
}) {
  return (
    <nav className="flex-1 px-3 py-4 space-y-1">
      {navigation.map((entry, idx) => {
        // Thin divider between different sections
        const prev = navigation[idx - 1];
        const showDivider =
          idx > 0 && (entry.type !== prev?.type || entry.type === "item");

        return (
          <div key={entry.type === "item" ? entry.href : entry.name}>
            {showDivider && (
              <div className="my-2 mx-2 border-t border-gray-200" />
            )}

            {entry.type === "item" ? (
              <Link
                to={entry.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActiveLink(entry.href)
                    ? "bg-primary-50 text-primary-700"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                )}
              >
                <entry.icon size={18} />
                <span>{entry.name}</span>
              </Link>
            ) : (
              <NavGroup
                entry={entry}
                isActiveLink={isActiveLink}
                isExpanded={expandedGroups.has(entry.name)}
                onToggle={() => toggleGroup(entry.name)}
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}

function NavGroup({
  entry,
  isActiveLink,
  isExpanded,
  onToggle,
}: {
  entry: NavGroupEntry;
  isActiveLink: (path: string) => boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const isGroupHeaderActive = isActiveLink(entry.href);
  const isAnyChildActive = entry.children.some((c) => isActiveLink(c.href));

  return (
    <div>
      {/* Group header: link + chevron toggle */}
      <div
        className={cn(
          "flex items-center rounded-lg transition-colors",
          isGroupHeaderActive
            ? "bg-primary-50 text-primary-700"
            : isAnyChildActive
              ? "text-gray-900"
              : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
        )}
      >
        <Link
          to={entry.href}
          className="flex-1 flex items-center gap-3 px-3 py-2.5 text-sm font-medium"
        >
          <entry.icon size={18} />
          <span>{entry.name}</span>
        </Link>
        {entry.children.length > 0 && (
          <button
            onClick={onToggle}
            className="p-2 mr-1 rounded-md hover:bg-black/5 transition-colors"
            aria-label={isExpanded ? "Dölj undermenyer" : "Visa undermenyer"}
          >
            <ChevronDown
              size={16}
              className={cn(
                "transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </button>
        )}
      </div>

      {/* Children — animated expand/collapse */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-in-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className="ml-[1.375rem] pl-3 border-l-2 border-gray-200 space-y-0.5 pt-1 pb-0.5">
            {entry.children.map((child) => (
              <Link
                key={child.href}
                to={child.href}
                className={cn(
                  "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-colors",
                  isActiveLink(child.href)
                    ? "bg-primary-50 text-primary-700 font-medium"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                )}
              >
                <child.icon size={15} />
                <span>{child.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Admin Layout
// ═══════════════════════════════════════════════════════════════════

export function AdminLayout() {
  const location = useLocation();
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const sidebarRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.hasAdminAccess || user?.role === "super_admin";

  // Fetch user's admin permissions
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    async function loadPermissions() {
      try {
        const res = await adminApi.getMyPermissions();
        if (!cancelled && res.success && res.data) {
          setUserPermissions(res.data);
        }
      } catch {
        // If permissions fail to load, allow super_admin based on user.role
        if (!cancelled) {
          setUserPermissions({
            permissions: {},
            isSuperAdmin: user?.role === "super_admin",
          });
        }
      } finally {
        if (!cancelled) setPermissionsLoading(false);
      }
    }
    loadPermissions();
    return () => { cancelled = true; };
  }, [isAdmin, user?.role]);

  // Filter navigation based on permissions
  const visibleNavigation = useMemo(() => {
    if (!userPermissions) return [] as NavEntry[];
    if (userPermissions.isSuperAdmin) return adminNavigation;

    return adminNavigation
      .map((entry): NavEntry | null => {
        if (entry.type === "item") {
          if (entry.permission === null) return null; // Dashboard: super_admin only
          const modulePerms = userPermissions.permissions[entry.permission];
          return modulePerms && modulePerms.length > 0 ? entry : null;
        }

        // Group: filter children individually
        const visibleChildren = entry.children.filter((child) => {
          if (child.permission === null) return false;
          const modulePerms = userPermissions.permissions[child.permission];
          return modulePerms && modulePerms.length > 0;
        });

        // Group header visible?
        const headerVisible =
          entry.permission !== null &&
          (userPermissions.permissions[entry.permission]?.length ?? 0) > 0;

        if (!headerVisible && visibleChildren.length === 0) return null;

        return { ...entry, children: visibleChildren };
      })
      .filter((e): e is NavEntry => e !== null);
  }, [userPermissions]);

  // First permitted path (for redirect)
  const firstPermittedPath = visibleNavigation.length > 0 ? visibleNavigation[0].href : null;

  const isActiveLink = useCallback(
    (path: string) => {
      if (path === "/admin") return location.pathname === "/admin";
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  // Auto-expand groups containing the active route
  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      for (const entry of adminNavigation) {
        if (entry.type === "group") {
          const isHeaderActive =
            location.pathname === entry.href ||
            location.pathname.startsWith(entry.href + "/");
          const isChildActive = entry.children.some((c) =>
            location.pathname.startsWith(c.href)
          );
          if (isHeaderActive || isChildActive) {
            next.add(entry.name);
          }
        }
      }
      return next;
    });
  }, [location.pathname]);

  const toggleGroup = useCallback((name: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on click outside (mobile)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsSidebarOpen(false);
      }
    };
    if (isSidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen]);

  // Prevent scroll when mobile sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isSidebarOpen]);

  // ── Early returns (after all hooks) ──────────────────────────────

  // Only admin and super_admin can access
  if (user && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show loading while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect non-super_admin from Dashboard (index) to first permitted page
  if (
    location.pathname === "/admin" &&
    userPermissions &&
    !userPermissions.isSuperAdmin &&
    firstPermittedPath &&
    firstPermittedPath !== "/admin"
  ) {
    return <Navigate to={firstPermittedPath} replace />;
  }

  // No accessible pages at all
  if (visibleNavigation.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500">Du har inte behörighet till några administrationssidor.</p>
          <Link to="/dashboard" className="text-primary-600 hover:text-primary-700 text-sm mt-2 inline-block">
            Tillbaka till dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Impersonation Banner */}
      <ImpersonationBanner />

      {/* Navbar - matching DashboardLayout style */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
                aria-label={isSidebarOpen ? "Stäng meny" : "Öppna meny"}
              >
                {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>

              <Link to="/admin" className="flex items-center space-x-3">
                <img src={logoSvg} alt="Kvanten" className="h-8 sm:h-10 w-auto" />
              </Link>

              {/* Admin indicator - subtle amber pill */}
              <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200/60 px-2.5 py-1 rounded-full text-xs font-medium">
                <Shield size={12} />
                <span>Administration</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500 hidden sm:block">
                {user?.firstName} {user?.lastName}
              </span>
              <Link
                to="/dashboard"
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Tillbaka</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex min-w-0">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex lg:flex-col lg:w-56 lg:fixed lg:inset-y-0 lg:top-16 bg-white border-r border-gray-200">
          <SidebarContent
            navigation={visibleNavigation}
            isActiveLink={isActiveLink}
            expandedGroups={expandedGroups}
            toggleGroup={toggleGroup}
          />
        </aside>

        {/* Sidebar - Mobile overlay */}
        {isSidebarOpen && (
          <div className="lg:hidden fixed inset-0 top-16 z-40 flex">
            <div
              ref={sidebarRef}
              className="w-64 bg-white border-r border-gray-200 shadow-xl"
            >
              <SidebarContent
                navigation={visibleNavigation}
                isActiveLink={isActiveLink}
                expandedGroups={expandedGroups}
                toggleGroup={toggleGroup}
              />
            </div>
            <div className="flex-1 bg-black/20" onClick={() => setIsSidebarOpen(false)} />
          </div>
        )}

        {/* Main content - min-w-0 allows flex child to shrink for scrollable tabs */}
        <main className="flex-1 lg:ml-56 min-w-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
